import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { SpotifyLogo, AppleLogo, InstagramLogo, YoutubeLogo, X, TiktokLogo, SoundcloudLogo } from '@phosphor-icons/react';
import ReactModal from 'react-modal';
import { initializeGA, sendPageview } from './utils/analytics';
import './App.css';
import logo from './assets/logo.svg';

import DesktopBackground from './DesktopBackground';
import ResponsiveSilhouette from './ResponsiveSilhouette';
import YouTubeChannelVideos from './YouTubeChannelVideos';
import LatestSpotifyReleases from './LatestSpotifyReleases';
// import ResidentAdvisorEventsGQL from './ResidentAdvisorEventsGQL';
import ContactInfo from './ContactInfo';
import SeatedEventsModal from './SeatedEventsModal';
import SignupModal from './SignupModal';
import CookiePopup from './CookiePopup';
// import NewsletterModal from './NewsletterModal'; 

// Bind modal to app root
ReactModal.setAppElement('#root');

// Modal animation variants
const modalVariants = {
  hidden: { 
    opacity: 0,
    x: '-100%'
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: { 
      duration: 0.5,
      ease: "easeOut",
      opacity: { duration: 0.3 },
      x: { duration: 0.5 }
    }
  },
  exit: {
    opacity: 0,
    x: '-100%',
    transition: {
      duration: 0.5,
      ease: "easeIn",
      opacity: { duration: 0.3 },
      x: { duration: 0.5 }
    }
  }
};

// Content variants for slide-in animation
const contentVariants = {
  hidden: { 
    opacity: 0,
    x: '-50%'
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      delay: 0.2,
      duration: 0.5,
      ease: "easeOut"
    }
  },
  exit: {
    opacity: 0,
    x: '-50%',
    transition: {
      duration: 0.3,
      ease: "easeIn"
    }
  }
};

// Close button animation variants
const closeButtonVariants = {
  hidden: { 
    opacity: 0,
    x: '-100%'
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      delay: 0.5,
      duration: 0.3,
    },
  },
  exit: {
    opacity: 0,
    x: '-100%',
    transition: {
      duration: 0,
    },
  },
};

// Main content animation variants
const mainContentVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0,
      ease: "easeIn",
    },
  },
};

// Main App Component
const App = () => {
  const [isContentVisible, setContentVisible] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [isModalAnimationComplete, setIsModalAnimationComplete] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const baseTitle = 'Adam Ten';
  const description = 'Official Adam Ten Website - Latest News, Music Releases, Tour Dates, and More';
  const currentUrl = 'https://www.adam-ten.com/';

  // Google Analytics Initialization (only if consent is given)
  useEffect(() => {
    initializeGA();
    sendPageview();
  }, []);

  // Track Page Views on Route Change (only if consent is given)
  useEffect(() => {
    sendPageview(location.pathname + location.search);
  }, [location]);

  // Memoize navigation links
  const navigationLinks = useMemo(() => [
    { label: 'SHOWS', component: <SeatedEventsModal /> },
    { label: 'VIDEOS', component: <YouTubeChannelVideos /> },
    { label: 'MUSIC', component: <LatestSpotifyReleases /> },
    { label: 'SIGN UP', component: <SignupModal /> },
    { label: 'CONTACT', component: <ContactInfo /> },
  ], []);

  // Memoize openModal and closeModal functions
  const openModal = useCallback((event, component, changeUrl = true) => {
    if (event) event.preventDefault();
    setModalContent(component);
    setModalOpen(true);

    if (changeUrl) {
      const newPath = navigationLinks
        .find((link) => link.component === component)
        .label
        .toLowerCase()
        .replace(/\s+/g, '-');
      navigate(`/${newPath}`);
    }
  }, [navigate, navigationLinks]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setIsModalAnimationComplete(false);
    navigate('/');
  }, [navigate]);

  // Handle initial content visibility with animation
  useEffect(() => {
    setContentVisible(true);
  }, []);

  // Handle modal state based on URL
  useEffect(() => {
    const modalPath = location.pathname.replace('/', '');
    if (modalPath) {
      const link = navigationLinks.find((link) => 
        link.label.toLowerCase().replace(/\s+/g, '-') === modalPath.toLowerCase()
      );
      if (link) {
        openModal(null, link.component, false);
      }
    }
  }, [location, navigationLinks, openModal]);

  // Function to lock scrolling when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isModalOpen]);

  // Handle Escape key press
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isModalOpen, closeModal]);

  return (
    <div className="content">
      <Helmet>
        <title>Adam Ten</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={baseTitle} />
        <meta property="og:description" content={description} />
        <meta name="twitter:title" content={baseTitle} />
        <meta name="twitter:description" content={description} />
        <link rel="canonical" href={currentUrl} />
        <meta property="og:url" content={currentUrl} />
      </Helmet>

      <motion.div 
        className="app"
        initial="hidden"
        animate={isContentVisible ? "visible" : "hidden"}
        variants={mainContentVariants}
      >

        <DesktopBackground />

        <header>
          <h1 className="logo">
            Adam Ten
            <img src={logo} alt="Logo" />
          </h1>
        </header>

        <nav className="navigation">
          {navigationLinks.map((link, index) => (
            <button
              key={index}
              onClick={(e) => openModal(e, link.component)}
              className="nav-link"
            >
              {link.label}
            </button>
          ))}
        </nav>

        <footer>
          <div className="social-icons">
            <a href="https://music.apple.com/us/artist/adam-ten/1472226854" target="_blank" rel="noopener noreferrer">
              <AppleLogo size={40} weight="fill" />
              Apple Music
            </a>
            <a href="https://open.spotify.com/artist/05tmGPn4fFdVpnsMt0YW5S" target="_blank" rel="noopener noreferrer">
              <SpotifyLogo size={40} weight="fill" />
              Spotify
            </a>
            <a href="https://soundcloud.com/adamten" target="_blank" rel="noopener noreferrer">
              <SoundcloudLogo size={40} weight="fill" />
              Soundcloud
            </a>
            <a href="https://www.instagram.com/adamten_" target="_blank" rel="noopener noreferrer">
              <InstagramLogo size={40} weight="fill" />
              Instagram
            </a>
            <a href="https://www.tiktok.com/@adamten_" target="_blank" rel="noopener noreferrer">
              <TiktokLogo size={40} weight="fill" />
              TikTok
            </a>
            <a href="https://www.youtube.com/@AdamTen" target="_blank" rel="noopener noreferrer">
              <YoutubeLogo size={40} weight="fill" />
              Youtube
            </a>
          </div>
        </footer>

        <ResponsiveSilhouette />

        <CookiePopup />

        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              className="modal-overlay"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={modalVariants}
              onAnimationComplete={() => setTimeout(() => setIsModalAnimationComplete(true), 800)}
            >
              <motion.button
                onClick={closeModal}
                className="close-button"
                aria-label="Close modal"
                variants={closeButtonVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <X size={34} weight="regular" aria-hidden="true" />
              </motion.button>

              <motion.div 
                className={`modal ${isModalAnimationComplete ? 'animation-complete' : ''}`}
                role="main" 
                variants={contentVariants}
              >
                {modalContent}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default App;